import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Image, ImageBackground, Modal, Platform, Pressable, ScrollView, StyleSheet, useWindowDimensions, View } from 'react-native';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import * as ImagePicker from 'expo-image-picker';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import {
  faChevronDown,
  faChild,
  faCalendar,
  faCircleCheck,
  faCircleExclamation,
  faCircleQuestion,
  faCircleUser,
  faFileArrowUp,
  faHourglassHalf,
  faIdCard,
  faPenToSquare,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import {
  AdultIdVerificationDto,
  FileParameter,
  MysteryShopperClient,
  MysteryShopperVerificationStatus,
  RelatedMinorDto,
  RelatedMinorGender,
  RelatedMinorRelationship,
} from '../../../api/api.g';
import CustomButton from '../../common/CustomButton';
import CustomDatePicker from '../../common/CustomDatePicker';
import CustomTextInput from '../../common/CustomTextInput';
import FieldErrorMessage from '../../common/FieldErrorMessage';
import InfoBox from '../../common/InfoBox';
import Label, { LabelType } from '../../common/Label';
import Loading from '../../common/Loading';
import MoreInfoButton from '../../common/MoreInfoButton';
import Overlay from '../../common/Overlay';
import MyAccountNavigationButton from '../MyAccountNavigationButton';
import MyAccountPageWrapper from '../MyAccountPageWrapper';
import NavigationElements from '../../../models/NavigationElements';
import useApi from '../../../hooks/api-hook';
import useCustomTheme from '../../../hooks/theme-hook';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hook';
import useTranslation from '../../../hooks/translation-hook';
import { lightBoxActions } from '../../../store/lightBoxSlice';
import { myAccountActions } from '../../../store/myAccountSlice';
import ProfileModal from '../myprofile/ProfileModal';

interface MinorFormState {
  relatedMinorId?: string;
  firstName: string;
  lastName: string;
  gender?: RelatedMinorGender;
  birthDate: string;
  relationship?: RelatedMinorRelationship;
  frontDocument?: FileParameter;
  backDocument?: FileParameter;
  photoDocument?: FileParameter;
}

const emptyMinorForm = (): MinorFormState => ({
  firstName: '',
  lastName: '',
  birthDate: '',
});

const statusKey = (status?: MysteryShopperVerificationStatus) => {
  switch (status) {
    case MysteryShopperVerificationStatus.Pending:
      return 'verification-status-pending';
    case MysteryShopperVerificationStatus.Approved:
      return 'verification-status-approved';
    case MysteryShopperVerificationStatus.Rejected:
      return 'verification-status-rejected';
    case MysteryShopperVerificationStatus.Invalidated:
      return 'verification-status-invalidated';
    case MysteryShopperVerificationStatus.DocumentsExpired:
      return 'verification-status-documents-expired';
    default:
      return 'verification-status-not-submitted';
  }
};

const formatDate = (value?: Date) => {
  if (!value) {
    return undefined;
  }

  return value.toLocaleDateString();
};

const relatedMinorDeletionDelayDays = 30;
const supportedIdDocumentAccept = 'image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif';
const supportedIdDocumentMimeTypes = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif']);
const supportedIdDocumentExtensions = new Set(['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif']);

const addDays = (value: Date, days: number) => {
  const result = new Date(value);
  result.setDate(result.getDate() + days);
  return result;
};

const canDeleteRelatedMinor = (minor: RelatedMinorDto) => {
  if (minor.submittedAt) {
    return addDays(minor.submittedAt, relatedMinorDeletionDelayDays).getTime() <= Date.now();
  }

  if (minor.documentsExpireAt) {
    return minor.documentsExpireAt.getTime() <= Date.now();
  }

  return !!minor.documentsDeletedAt;
};

const getFileExtension = (fileName?: string) => {
  if (!fileName) {
    return '';
  }

  const extensionIndex = fileName.lastIndexOf('.');
  return extensionIndex >= 0 ? fileName.substring(extensionIndex).toLowerCase() : '';
};

const getMimeTypeFromFileName = (fileName?: string) => {
  switch (getFileExtension(fileName)) {
    case '.jpg':
    case '.jpeg':
      return 'image/jpeg';
    case '.png':
      return 'image/png';
    case '.webp':
      return 'image/webp';
    case '.heic':
      return 'image/heic';
    case '.heif':
      return 'image/heif';
    default:
      return undefined;
  }
};

const isSupportedIdDocumentFile = (file: { name?: string; type?: string }) => {
  const contentType = file.type?.trim().toLowerCase();

  if (contentType) {
    return supportedIdDocumentMimeTypes.has(contentType);
  }

  return supportedIdDocumentExtensions.has(getFileExtension(file.name));
};

const verificationErrorMessage = (error: any, t: (key: string) => string) => {
  const response = error?.response ?? error?.result ?? error?.data;
  const data = typeof response === 'string' ? tryParseJson(response) : response;
  const validationMessages = extractValidationErrorMessages(data, t);

  if (validationMessages.length > 0) {
    return Array.from(new Set(validationMessages)).join('\n');
  }

  if (error?.isAxiosError && !error?.response) {
    return t('Something-went-wrong-please-try-again-later');
  }

  const title = typeof data?.title === 'string' && data.title !== 'One or more validation errors occurred.' ? data.title : undefined;
  const message = data?.message ?? data?.detail ?? title ?? (typeof data === 'string' ? data : undefined) ?? error?.message;

  if (typeof message === 'string' && message.startsWith('id-verification-error-')) {
    return t(message);
  }

  if (message === 'Something-went-wrong-please-try-again-later') {
    return t(message);
  }

  if (message === 'Network Error') {
    return t('Something-went-wrong-please-try-again-later');
  }

  if (typeof message === 'string' && message) {
    return message;
  }

  return t('Something-went-wrong-please-try-again-later');
};

const extractValidationErrorMessages = (data: any, t: (key: string) => string): string[] => {
  const errors = data?.errors;

  if (!errors) {
    return [];
  }

  return flattenValidationMessages(errors)
    .map((message) => message.trim())
    .filter(Boolean)
    .map((message) => (message.startsWith('id-verification-error-') ? t(message) : message));
};

const flattenValidationMessages = (value: any): string[] => {
  if (!value) {
    return [];
  }

  if (typeof value === 'string') {
    return [value];
  }

  if (Array.isArray(value)) {
    return value.flatMap(flattenValidationMessages);
  }

  if (typeof value === 'object') {
    return Object.values(value).flatMap(flattenValidationMessages);
  }

  return [];
};

const tryParseJson = (value: string) => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const IdVerification = () => {
  const t = useTranslation();
  const theme = useCustomTheme();
  const dispatch = useAppDispatch();
  const returnToMission = useAppSelector((selector) => selector.myAccount.returnDestination === 'mission');
  const navigation = useNavigation();
  const api = useApi(MysteryShopperClient);
  const insets = useSafeAreaInsets();
  const layout = useWindowDimensions();
  const bottomContentPadding = Platform.OS === 'web' ? 16 : Math.max(insets.bottom, 0) + 56;
  const [adultFront, setAdultFront] = useState<FileParameter>();
  const [adultBack, setAdultBack] = useState<FileParameter>();
  const [minorForm, setMinorForm] = useState<MinorFormState>(emptyMinorForm());
  const [showMinorForm, setShowMinorForm] = useState(false);
  const [showAdultVerificationForm, setShowAdultVerificationForm] = useState(false);
  const [minorEditField, setMinorEditField] = useState<'name' | 'birthDate'>();
  const [minorNameDraft, setMinorNameDraft] = useState({ firstName: '', lastName: '' });
  const [minorBirthDateDraft, setMinorBirthDateDraft] = useState('');
  const [minorFieldMessage, setMinorFieldMessage] = useState<string>();
  const [message, setMessage] = useState<string>();
  const [minorMessage, setMinorMessage] = useState<string>();
  const [selectorField, setSelectorField] = useState<'gender' | 'relationship'>();
  const [adultSubmitting, setAdultSubmitting] = useState(false);
  const [minorSubmitting, setMinorSubmitting] = useState(false);
  const [deletingMinorId, setDeletingMinorId] = useState<string>();
  const fileInputRefs = useRef<Record<string, any>>({});
  const selectorOpenLockedUntilRef = useRef(0);

  const statusQuery = useQuery({
    enabled: true,
    queryKey: ['mysteryShopperIdVerification'],
    queryFn: () => api.getIdVerification(api.tokenSource?.token),
    onError: (error) => {
      showError(verificationErrorMessage(error, t));
    },
  });

  useFocusEffect(
    useCallback(() => {
      setMessage(undefined);
      setMinorMessage(undefined);
      void statusQuery.refetch();

      return undefined;
    }, [statusQuery.refetch]),
  );

  const submitAdultDocuments = async () => {
    if (!adultFront || !adultBack || adultSubmitting) {
      return;
    }

    setAdultSubmitting(true);

    try {
      await api.uploadIdVerification(adultFront, adultBack, api.tokenSource?.token);
      showSuccess(t('documents-uploaded'));
      setAdultFront(undefined);
      setAdultBack(undefined);
      await statusQuery.refetch();
    } catch (error) {
      showError(verificationErrorMessage(error, t));
    } finally {
      setAdultSubmitting(false);
    }
  };

  const submitMinor = async (minor: MinorFormState) => {
    const frontDocument = minor.frontDocument;
    const backDocument = minor.backDocument;
    const photoDocument = minor.photoDocument;

    if (minorSubmitting || !frontDocument || !backDocument || minor.gender == null || minor.relationship == null) {
      return;
    }

    setMinorSubmitting(true);

    try {
      await api.submitRelatedMinors(
        minor.relatedMinorId ? [minor.relatedMinorId] : [],
        [minor.firstName],
        [minor.lastName],
        [`${minor.gender}`],
        [minor.birthDate],
        [`${minor.relationship}`],
        [frontDocument],
        [backDocument],
        photoDocument ? [photoDocument] : [],
        api.tokenSource?.token,
      );
      showSuccess(t('minor-documents-uploaded'));
      setMinorForm(emptyMinorForm());
      setShowMinorForm(false);
      await statusQuery.refetch();
    } catch (error) {
      showMinorError(verificationErrorMessage(error, t));
    } finally {
      setMinorSubmitting(false);
    }
  };

  const submitDeleteMinor = async (relatedMinorId: string) => {
    if (deletingMinorId) {
      return;
    }

    setDeletingMinorId(relatedMinorId);

    try {
      await api.deleteRelatedMinor(relatedMinorId, api.tokenSource?.token);
      showSuccess(t('related-minor-deleted'));
      setMinorForm(emptyMinorForm());
      setShowMinorForm(false);
      await statusQuery.refetch();
    } catch (error) {
      showError(verificationErrorMessage(error, t));
    } finally {
      setDeletingMinorId(undefined);
    }
  };

  const adultVerification = statusQuery.data?.adultVerification;
  const relatedMinors = statusQuery.data?.relatedMinors ?? [];
  const adultStatus = adultVerification?.status;
  const hasAdultVerificationState =
    !!adultVerification?.id ||
    adultStatus != null ||
    adultVerification?.submittedAt != null ||
    adultVerification?.documentsExpireAt != null ||
    adultVerification?.documentsDeletedAt != null;
  const adultIsSoftVerified = !!adultVerification?.isSoftVerified;
  const adultApproved = adultVerification?.status === MysteryShopperVerificationStatus.Approved;
  const adultPendingReview = adultStatus === MysteryShopperVerificationStatus.Pending;
  const adultNeedsResubmission =
    adultStatus === MysteryShopperVerificationStatus.Rejected ||
    adultStatus === MysteryShopperVerificationStatus.Invalidated ||
    adultStatus === MysteryShopperVerificationStatus.DocumentsExpired;
  const canUseWebFileInput = Platform.OS === 'web';
  const canSubmitAdult = !!adultVerification?.canSubmit;
  const shouldShowIntro = !showAdultVerificationForm && !hasAdultVerificationState;
  const shouldShowVerificationSteps = (showAdultVerificationForm && !hasAdultVerificationState) || adultPendingReview || adultNeedsResubmission || adultApproved;
  const adultCanUploadInSteps = !hasAdultVerificationState || adultNeedsResubmission;

  const adultStatusText = useMemo(() => t(statusKey(adultVerification?.status)), [adultVerification?.status, t]);
  const backgroundPrimary = theme.background?.primary ?? '#2d3f4c';
  const backgroundModal = theme.background?.modal ?? backgroundPrimary;
  const backgroundSea = theme.background?.sea ?? theme.background?.darkTurquoise ?? '#0083a9';
  const backgroundLightSea = theme.background?.lightSea ?? backgroundPrimary;
  const textPrimary = theme.text?.primary ?? '#ffffff';
  const textDarkTurquoiseTwo = theme.text?.darkTurquoiseTwo ?? '#5fc8ba';
  const textError = theme.text?.error ?? theme.colorErrorPrimary ?? '#a51642';
  const inputTextPlaceholder = theme.inputTextPlaceholder ?? textPrimary;
  const stepCardBackground = theme.isDark ? backgroundModal : '#e8f4f2';
  const stepTextColor = theme.isDark ? textPrimary : '#172126';
  const stepTitleColor = theme.text?.titleDark ?? '#00546a';
  const retentionColor = theme.isDark ? textError : theme.colorErrorPrimary ?? textError;

  const fieldBaseStyle = {
    borderWidth: 1,
    borderColor: theme.borderColor,
    borderRadius: 6,
    minHeight: 40,
    paddingHorizontal: 8,
    backgroundColor: backgroundPrimary,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
  };

  const createFileChangeHandler = (setValue: (value: FileParameter) => void, showInvalidFileMessage: (value: string) => void) => (event: any) => {
    const file = event?.target?.files?.[0];

    if (!file) {
      return;
    }

    if (!isSupportedIdDocumentFile(file)) {
      clearMessage();
      showInvalidFileMessage(t('field_value_not_valid'));
      return;
    }

    clearMessage();
    setValue({ data: file, fileName: file.name });
  };

  const openFileInput = (inputKey: string) => {
    const input = fileInputRefs.current[inputKey];

    if (!input) {
      return;
    }

    input.value = '';
    input.click?.();
  };

  const pickNativePhoto = async (setValue: (value: FileParameter) => void) => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      allowsMultipleSelection: false,
      preferredAssetRepresentationMode: ImagePicker.UIImagePickerPreferredAssetRepresentationMode.Compatible,
      quality: 0.9,
    });

    if (result.canceled) {
      return;
    }

    const asset = result.assets?.[0];

    if (!asset?.uri) {
      return;
    }

    const fileName = asset.fileName || asset.uri.split('/').pop() || 'photo';
    const fileData = {
      uri: asset.uri,
      name: fileName,
      type: asset.mimeType || getMimeTypeFromFileName(fileName) || 'application/octet-stream',
    } as unknown as Blob;

    clearMessage();
    setValue({ data: fileData, fileName });
  };

  function showSuccess(value: string) {
    clearMessage();
    dispatch(
      lightBoxActions.setContent(
        <ProfileModal width={340} title={t('success')} onClose={closeSuccessModal} onConfirm={closeSuccessModal}>
          <View>
            <Label style={{ marginBottom: 8 }} numberOfLines={5}>
              {value}
            </Label>
          </View>
        </ProfileModal>,
      ),
    );
    dispatch(lightBoxActions.setVisibility(true));
  }

  function showError(value: string) {
    setMessage(value);
  }

  function showMinorError(value: string) {
    setMinorMessage(value);
  }

  function clearMessage() {
    setMessage(undefined);
    setMinorMessage(undefined);
  }

  function closeSuccessModal() {
    dispatch(lightBoxActions.setVisibility(false));
    dispatch(lightBoxActions.setContent(<></>));
  }

  const updateMinor = <TKey extends keyof MinorFormState>(field: TKey, value: MinorFormState[TKey]) => {
    setMinorMessage(undefined);
    setMinorForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const closeSelector = () => {
    selectorOpenLockedUntilRef.current = Date.now() + 250;
    setSelectorField(undefined);
  };

  const openSelector = (field: 'gender' | 'relationship') => {
    if (Date.now() < selectorOpenLockedUntilRef.current) {
      return;
    }

    setSelectorField(field);
  };

  const resetMinorForm = () => {
    setMinorForm(emptyMinorForm());
    setShowMinorForm(false);
    setMinorEditField(undefined);
    setMinorFieldMessage(undefined);
  };

  const openAdultVerificationForm = () => {
    setShowAdultVerificationForm(true);
    clearMessage();
  };

  const goBack = () => {
    if (returnToMission) {
      dispatch(myAccountActions.setVisibility(false));
      return;
    }

    navigation.navigate(NavigationElements.menu as never);
  };

  const backNavigationProps = returnToMission
    ? { navigationTitle: t('back'), navigationOnPress: goBack }
    : { navigationTitle: t('my_profile'), navigationAction: NavigationElements.menu };

  const openHelp = () => {
    navigation.navigate(NavigationElements.help as never);
  };

  const titleHelpButton = (
    <Pressable accessibilityRole="button" accessibilityLabel={t('learn-more')} hitSlop={8} onPress={openHelp} style={{ marginLeft: 8 }}>
      <FontAwesomeIcon icon={faCircleQuestion} size={18} color={String(stepTitleColor)} />
    </Pressable>
  );

  const goToProfileInformation = () => {
    navigation.navigate(NavigationElements.myProfile as never);
  };

  const validateAndSaveMinor = () => {
    if (
      !minorForm.firstName ||
      !minorForm.lastName ||
      minorForm.gender == null ||
      minorForm.relationship == null ||
      !minorForm.birthDate ||
      !minorForm.frontDocument ||
      !minorForm.backDocument
    ) {
      showMinorError(t('all-minor-fields-required'));
      return;
    }

    clearMessage();
    void submitMinor(minorForm);
  };

  const startMinorAdd = () => {
    setMinorForm(emptyMinorForm());
    setShowMinorForm(true);
    clearMessage();
  };

  const startMinorEdit = (minor: RelatedMinorDto) => {
    setMinorForm({
      relatedMinorId: minor.id,
      firstName: minor.firstName ?? '',
      lastName: minor.lastName ?? '',
      gender: minor.gender,
      birthDate: minor.birthDate ? minor.birthDate.toISOString().substring(0, 10) : '',
      relationship: minor.relationship,
    });
    setShowMinorForm(true);
    clearMessage();
  };

  const openMinorNamePopup = () => {
    setMinorNameDraft({ firstName: minorForm.firstName, lastName: minorForm.lastName });
    setMinorFieldMessage(undefined);
    setMinorEditField('name');
  };

  const openMinorBirthDatePopup = () => {
    setMinorBirthDateDraft(minorForm.birthDate);
    setMinorFieldMessage(undefined);
    setMinorEditField('birthDate');
  };

  const closeMinorFieldPopup = () => {
    setMinorEditField(undefined);
    setMinorFieldMessage(undefined);
  };

  const saveMinorFieldPopup = () => {
    if (minorEditField === 'name') {
      const firstName = minorNameDraft.firstName.trim();
      const lastName = minorNameDraft.lastName.trim();

      if (!firstName || !lastName) {
        setMinorFieldMessage(t('id-verification-error-minor-name-required'));
        return;
      }

      setMinorForm((current) => ({
        ...current,
        firstName,
        lastName,
      }));
      setMinorMessage(undefined);
      closeMinorFieldPopup();
      return;
    }

    if (minorEditField === 'birthDate') {
      if (!minorBirthDateDraft) {
        setMinorFieldMessage(t('field_required'));
        return;
      }

      updateMinor('birthDate', minorBirthDateDraft);
      closeMinorFieldPopup();
    }
  };

  const deleteMinor = (minor: RelatedMinorDto) => {
    const relatedMinorId = minor.id;

    if (!relatedMinorId) {
      return;
    }

    dispatch(
      lightBoxActions.setContent(
        <ProfileModal
          width={340}
          hideConfirm
          showCancel
          showDelete
          title={t('delete')}
          onCancel={closeSuccessModal}
          onClose={closeSuccessModal}
          onDelete={() => {
            closeSuccessModal();
            void submitDeleteMinor(relatedMinorId);
          }}
        >
          <View>
            <Label style={{ marginBottom: 8 }} numberOfLines={5}>
              {t('confirm-delete-related-minor')}
            </Label>
          </View>
        </ProfileModal>,
      ),
    );
    dispatch(lightBoxActions.setVisibility(true));
  };

  const renderSelect = <TValue extends number>(
    field: 'gender' | 'relationship',
    value: TValue,
    options: { value: TValue; label: string }[],
  ) => {
    const selectedOption = options.find((x) => x.value === value);

    return (
      <Pressable onPress={() => openSelector(field)} style={[fieldBaseStyle, { marginTop: 6, marginBottom: 12 }]}>
        <Label style={{ color: selectedOption ? textPrimary : inputTextPlaceholder }}>{selectedOption?.label ?? ''}</Label>
        <FontAwesomeIcon icon={faChevronDown} size={14} color={String(textDarkTurquoiseTwo)} />
      </Pressable>
    );
  };

  const renderFileInput = (inputKey: string, label: string, value: FileParameter | undefined, onChange: (value: FileParameter) => void) => (
    <View style={{ marginTop: 12 }}>
      <Label variant={LabelType.titleSmall}>{label}</Label>
      {canUseWebFileInput ? (
        <>
          {React.createElement('input', {
            ref: (element: any) => {
              fileInputRefs.current[inputKey] = element;
            },
            type: 'file',
            accept: supportedIdDocumentAccept,
            onChange: createFileChangeHandler(onChange, showError),
            style: { display: 'none' },
          })}
          <Pressable onPress={() => openFileInput(inputKey)} style={[fieldBaseStyle, { marginTop: 6 }]}>
            <Label numberOfLines={1} style={{ color: value?.fileName ? textPrimary : inputTextPlaceholder, flexShrink: 1 }}>
              {value?.fileName ?? t('choose-file')}
            </Label>
          </Pressable>
        </>
      ) : (
        <Pressable onPress={() => pickNativePhoto(onChange)} style={[fieldBaseStyle, { marginTop: 6 }]}>
          <Label numberOfLines={1} style={{ color: value?.fileName ? textPrimary : inputTextPlaceholder, flexShrink: 1 }}>
            {value?.fileName ?? t('choose-file')}
          </Label>
        </Pressable>
      )}
    </View>
  );

  const renderStepHeader = (icon: any, title: string, completed = false) => (
    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', flexShrink: 1 }}>
        <FontAwesomeIcon icon={icon} size={17} color={String(stepTitleColor)} />
        <Label
          variant={LabelType.titleLargeNoCapsPrimary}
          style={{ color: stepTitleColor, fontSize: 16, lineHeight: 20, marginLeft: 8, textTransform: 'uppercase', flexShrink: 1 }}
          numberOfLines={2}
        >
          {title}
        </Label>
      </View>
      {completed && <FontAwesomeIcon icon={faCircleCheck} size={26} color={String(textDarkTurquoiseTwo)} />}
    </View>
  );

  const renderStepCard = (content: React.ReactNode, locked = false) => {
    const style = {
      borderRadius: 8,
      overflow: 'hidden' as const,
      backgroundColor: stepCardBackground,
      marginTop: 10,
    };

    if (locked) {
      return (
        <ImageBackground
          source={require('../../../../assets/disabledBars.png')}
          style={style}
          imageStyle={{ resizeMode: 'repeat', opacity: theme.isDark ? 0.12 : 0.38 }}
        >
          <View style={{ padding: 14 }}>{content}</View>
        </ImageBackground>
      );
    }

    return <View style={[style, { padding: 14 }]}>{content}</View>;
  };

  const renderStepFileButton = (
    inputKey: string,
    label: string,
    value: FileParameter | undefined,
    onChange: (value: FileParameter) => void,
  ) => {
    const displayLabel = value?.fileName ?? label;
    const showInvalidFileMessage = inputKey.startsWith('minor-') ? showMinorError : showError;

    const button = (
      <Pressable
        onPress={() => {
          if (canUseWebFileInput) {
            openFileInput(inputKey);
            return;
          }

          void pickNativePhoto(onChange);
        }}
        style={{
          borderWidth: 1,
          borderColor: textDarkTurquoiseTwo,
          borderRadius: 8,
          minHeight: 36,
          minWidth: 116,
          maxWidth: '100%',
          paddingHorizontal: 14,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <FontAwesomeIcon icon={faFileArrowUp} size={14} color={String(textDarkTurquoiseTwo)} />
        <Label numberOfLines={1} style={{ color: textDarkTurquoiseTwo, fontWeight: '700', flexShrink: 1 }}>
          {displayLabel}
        </Label>
      </Pressable>
    );

    if (!canUseWebFileInput) {
      return button;
    }

    return (
      <>
        {React.createElement('input', {
          ref: (element: any) => {
            fileInputRefs.current[inputKey] = element;
          },
          type: 'file',
          accept: supportedIdDocumentAccept,
          onChange: createFileChangeHandler(onChange, showInvalidFileMessage),
          style: { display: 'none' },
        })}
        {button}
      </>
    );
  };

  const renderAdultStepStatus = () => {
    if (!hasAdultVerificationState) {
      return null;
    }

    if (adultApproved) {
      return (
        <View style={{ marginTop: 12 }}>
          {!!adultVerification?.documentsDeletedAt && (
            <Label style={{ color: stepTextColor, fontStyle: 'italic' }}>
              {t('documents-deleted-at', { value: formatDate(adultVerification.documentsDeletedAt) })}
            </Label>
          )}
          {!adultVerification?.documentsDeletedAt && !!adultVerification?.documentsExpireAt && (
            <Label style={{ color: stepTextColor, fontStyle: 'italic' }}>
              {t('documents-expire-at', { value: formatDate(adultVerification.documentsExpireAt) })}
            </Label>
          )}
        </View>
      );
    }

    return (
      <View style={{ marginTop: 12 }}>
        <Label variant={LabelType.titleSmall} style={statusLabelStyle(adultVerification?.status)}>
          {adultStatusText}
        </Label>
        {!!adultVerification?.documentsExpireAt && (
          <Label style={{ color: stepTextColor, marginTop: 8 }}>
            {t('documents-expire-at', { value: formatDate(adultVerification.documentsExpireAt) })}
          </Label>
        )}
        {!!adultVerification?.documentsDeletedAt && (
          <Label style={{ color: stepTextColor, marginTop: 8 }}>
            {t('documents-deleted-at', { value: formatDate(adultVerification.documentsDeletedAt) })}
          </Label>
        )}
        {renderStatusReason(adultVerification?.status, adultVerification?.reviewReason, adultVerification?.invalidationReason)}
      </View>
    );
  };

  const renderOutlineStepButton = (title: string, onPress: () => void) => (
    <Pressable
      onPress={onPress}
      style={{
        alignSelf: 'flex-start',
        borderWidth: 2,
        borderColor: textDarkTurquoiseTwo,
        borderRadius: 18,
        minHeight: 36,
        paddingHorizontal: 14,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginTop: 16,
      }}
    >
      <FontAwesomeIcon icon={faPlus} size={16} color={String(textDarkTurquoiseTwo)} />
      <Label style={{ color: textDarkTurquoiseTwo, fontWeight: '700' }}>{title}</Label>
    </Pressable>
  );

  const renderMinorStepContent = () => {
    if (!adultApproved) {
      return renderStepCard(
        <>
          {renderStepHeader(faChild, t('id-verification-minor-step-title'))}
          <Label style={{ color: stepTextColor, fontSize: 14, lineHeight: 18, marginTop: 12 }} numberOfLines={5}>
            {t('id-verification-minor-step-locked-body')}
          </Label>
        </>,
        true,
      );
    }

    return renderStepCard(
      <>
        {renderStepHeader(faChild, t('id-verification-minor-step-title'))}
        {relatedMinors.length === 0 && (
          <Label style={{ color: stepTextColor, fontSize: 14, lineHeight: 18, marginTop: 12 }} numberOfLines={5}>
            {t('id-verification-minor-step-active-body')}
          </Label>
        )}
        {relatedMinors.length === 0 && !showMinorForm && renderOutlineStepButton(t('id-verification-minor-step-title'), startMinorAdd)}
        {relatedMinors.map(renderMinorRow)}
        {adultApproved && !showMinorForm && relatedMinors.length > 0 && renderOutlineStepButton(t('id-verification-minor-step-title'), startMinorAdd)}
        {showMinorForm && (
          <>
            {relatedMinors.length > 0 && (
              <Label style={{ color: stepTextColor, fontSize: 14, lineHeight: 18, marginTop: 12 }} numberOfLines={5}>
                {t('id-verification-minor-step-active-body')}
              </Label>
            )}
            {renderMinorForm()}
          </>
        )}
      </>,
    );
  };

  const renderVerificationSteps = () => (
    <View style={{ paddingBottom: bottomContentPadding }}>
      {!!message && (
        <View style={{ paddingTop: 8, marginBottom: 16 }}>
          <FieldErrorMessage message={message} />
        </View>
      )}

      <Label style={{ color: stepTextColor, fontSize: 15, lineHeight: 20, marginBottom: 18 }} numberOfLines={5}>
        {t('id-verification-intro-description')}
      </Label>
      <Label style={{ color: stepTitleColor, fontSize: 12, lineHeight: 16, marginBottom: 4 }}>{t('id-verification-steps-title')}</Label>

      {renderStepCard(
        adultIsSoftVerified ? (
          renderStepHeader(faCircleUser, t('id-verification-profile-step-title'), true)
        ) : (
          <>
            {renderStepHeader(faCircleUser, t('id-verification-profile-step-title'))}
            <Label style={{ color: stepTextColor, fontSize: 14, lineHeight: 18, marginTop: 12 }} numberOfLines={4}>
              {t('id-verification-profile-step-body')}
            </Label>
            <CustomButton
              title={t('id-verification-profile-step-cta')}
              onPress={goToProfileInformation}
              style={{ alignSelf: 'center', width: 180, height: 44, maxHeight: 44, marginTop: 14 }}
              textStyle={{ color: 'white' }}
            />
          </>
        ),
      )}

      {adultIsSoftVerified
        ? renderStepCard(
            <>
              {renderStepHeader(faIdCard, t('id-verification-identity-step-title'), adultApproved)}
              {renderAdultStepStatus()}
              {adultCanUploadInSteps && (
                <>
                  <Label style={{ color: stepTextColor, fontSize: 14, lineHeight: 18, marginTop: 12 }} numberOfLines={3}>
                    {t('id-verification-identity-step-body')}
                  </Label>
                  <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginTop: 16 }}>
                    {renderStepFileButton('adult-front-document-step', t('id-verification-front-short'), adultFront, setAdultFront)}
                    {renderStepFileButton('adult-back-document-step', t('id-verification-back-short'), adultBack, setAdultBack)}
                  </View>
                  <CustomButton
                    title={t(adultNeedsResubmission ? 'resubmit-documents' : 'submit-documents')}
                    onPress={submitAdultDocuments}
                    disabled={!adultFront || !adultBack || adultSubmitting || !canSubmitAdult}
                    style={{ marginTop: 16 }}
                  />
                </>
              )}
            </>,
          )
        : renderStepCard(
            <>
              {renderStepHeader(faIdCard, t('id-verification-identity-step-title'))}
              <Label style={{ color: stepTextColor, fontSize: 14, lineHeight: 18, marginTop: 12 }} numberOfLines={4}>
                {t('id-verification-identity-step-locked-body')}
              </Label>
            </>,
            true,
          )}

      {renderMinorStepContent()}

      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginTop: 18 }}>
        <FontAwesomeIcon icon={faCircleExclamation} size={28} color={String(retentionColor)} />
        <Label style={{ color: retentionColor, flex: 1, fontSize: 14, lineHeight: 18 }} numberOfLines={4}>
          {t(showMinorForm ? 'id-verification-minor-retention' : 'id-verification-intro-retention')}
        </Label>
      </View>

      <Pressable accessibilityRole="button" onPress={openHelp} style={{ alignSelf: 'flex-start', marginTop: 34, paddingVertical: 4 }}>
        <Label style={{ color: stepTitleColor, fontSize: 16, lineHeight: 20 }}>{t('learn-more')}</Label>
      </Pressable>
      {renderSelectorModal()}
      {renderMinorFieldModal()}
    </View>
  );

  const statusColor = (status?: MysteryShopperVerificationStatus) => {
    switch (status) {
      case MysteryShopperVerificationStatus.Approved:
        return textDarkTurquoiseTwo;
      case MysteryShopperVerificationStatus.Pending:
        return theme.primary;
      case MysteryShopperVerificationStatus.Rejected:
      case MysteryShopperVerificationStatus.Invalidated:
      case MysteryShopperVerificationStatus.DocumentsExpired:
        return theme.isDark ? textError : theme.colorErrorPrimary ?? textError;
      default:
        return textPrimary;
    }
  };

  const statusLabelStyle = (status?: MysteryShopperVerificationStatus, marginTop = 0) => ({
    marginTop,
    color: statusColor(status),
    fontWeight: '700' as const,
  });

  const minorStatusIcon = (status?: MysteryShopperVerificationStatus) => {
    switch (status) {
      case MysteryShopperVerificationStatus.Pending:
        return faHourglassHalf;
      case MysteryShopperVerificationStatus.Approved:
        return faCircleCheck;
      case MysteryShopperVerificationStatus.Rejected:
      case MysteryShopperVerificationStatus.Invalidated:
      case MysteryShopperVerificationStatus.DocumentsExpired:
        return faCircleExclamation;
      default:
        return faCircleQuestion;
    }
  };

  const renderReason = (labelKey: string, value?: string) => {
    if (!value) {
      return null;
    }

    return (
      <View style={{ marginTop: 8 }}>
        <Label style={{ color: textDarkTurquoiseTwo, fontWeight: '700' }}>{t(labelKey)}</Label>
        <Label style={{ marginTop: 2, color: theme.isDark ? textError : theme.colorErrorPrimary ?? textError }} numberOfLines={5}>
          {value}
        </Label>
      </View>
    );
  };

  const renderStatusReason = (status?: MysteryShopperVerificationStatus, reviewReason?: string, invalidationReason?: string) => (
    <>
      {status === MysteryShopperVerificationStatus.Rejected && renderReason('rejection-reason', reviewReason)}
      {status === MysteryShopperVerificationStatus.Invalidated && renderReason('invalidation-reason', invalidationReason)}
    </>
  );

  const renderAdultStatus = (verification?: AdultIdVerificationDto) => (
    <View>
      <Label variant={LabelType.titleSmall} style={statusLabelStyle(verification?.status)}>
        {adultStatusText}
      </Label>
      {!!verification?.documentsExpireAt && (
        <Label style={{ marginTop: 8 }}>{t('documents-expire-at', { value: formatDate(verification.documentsExpireAt) })}</Label>
      )}
      {!!verification?.documentsDeletedAt && (
        <Label style={{ marginTop: 8 }}>{t('documents-deleted-at', { value: formatDate(verification.documentsDeletedAt) })}</Label>
      )}
      {renderStatusReason(verification?.status, verification?.reviewReason, verification?.invalidationReason)}
    </View>
  );

  const genderOptions = [
    { value: RelatedMinorGender.Male, label: t('related-minor-gender-male') },
    { value: RelatedMinorGender.Female, label: t('related-minor-gender-female') },
    { value: RelatedMinorGender.NonBinary, label: t('related-minor-gender-non-binary') },
  ];

  const relationshipOptions = [
    { value: RelatedMinorRelationship.Child, label: t('related-minor-relationship-child') },
    { value: RelatedMinorRelationship.Sibling, label: t('related-minor-relationship-sibling') },
    { value: RelatedMinorRelationship.LegalGuardian, label: t('related-minor-relationship-legal-guardian') },
    { value: RelatedMinorRelationship.OtherRelatedFamily, label: t('related-minor-relationship-other-related-family') },
  ];

  const genderLabel = (gender?: RelatedMinorGender) => genderOptions.find((x) => x.value === gender)?.label;
  const relationshipLabel = (relationship?: RelatedMinorRelationship) => relationshipOptions.find((x) => x.value === relationship)?.label;
  const minorNameValue = `${minorForm.firstName ?? ''} ${minorForm.lastName ?? ''}`.trim();

  const renderMinorFieldRow = (label: string, value: string | undefined, onPress: () => void, icon = faPenToSquare) => (
    <Pressable onPress={onPress} style={{ paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: theme.borderColorLight }}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12 }}>
        <View style={{ flex: 1 }}>
          <Label style={{ color: stepTitleColor, fontSize: 12, lineHeight: 16 }}>{label}</Label>
          <Label style={{ color: value ? stepTextColor : inputTextPlaceholder, fontSize: 14, lineHeight: 18, marginTop: 2 }} numberOfLines={2}>
            {value || t('add')}
          </Label>
        </View>
        <FontAwesomeIcon icon={icon} size={14} color={String(textDarkTurquoiseTwo)} />
      </View>
    </Pressable>
  );

  const renderMinorRow = (minor: RelatedMinorDto) => {
    const minorName = `${minor.firstName ?? ''} ${minor.lastName ?? ''}`.trim();
    const minorDetails = [genderLabel(minor.gender), relationshipLabel(minor.relationship)].filter(Boolean).join(' - ');
    const isDeleting = deletingMinorId === minor.id;
    const showDeleteMinor = adultApproved && canDeleteRelatedMinor(minor);

    return (
      <View key={minor.id ?? `${minor.firstName}-${minor.lastName}`} style={{ paddingTop: 12, paddingBottom: 10, borderTopWidth: 1, borderColor: theme.borderColorLight }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderBottomColor: theme.borderColorLight, paddingBottom: 6 }}>
          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 7 }}>
            <FontAwesomeIcon icon={minorStatusIcon(minor.status)} size={15} color={String(statusColor(minor.status))} />
            <Label variant={LabelType.titleSmall} style={{ color: stepTextColor, flexShrink: 1 }} numberOfLines={2}>
              {minorName}
            </Label>
          </View>
          {adultApproved && (
            <Pressable onPress={() => startMinorEdit(minor)} hitSlop={10} style={{ paddingLeft: 10, paddingVertical: 2 }}>
              <FontAwesomeIcon icon={faPenToSquare} size={14} color={String(textDarkTurquoiseTwo)} />
            </Pressable>
          )}
        </View>

        {!!minorDetails && (
          <Label style={{ color: stepTextColor, marginTop: 8, fontStyle: 'italic' }}>
            {minorDetails}
          </Label>
        )}
        {!!minor.birthDate && <Label style={{ color: stepTextColor, marginTop: 4 }}>{formatDate(minor.birthDate)}</Label>}
        {!!minor.documentsDeletedAt && (
          <Label style={{ color: stepTextColor, marginTop: 4, fontStyle: 'italic' }}>
            {t('documents-deleted-at', { value: formatDate(minor.documentsDeletedAt) })}
          </Label>
        )}
        {!minor.documentsDeletedAt && !!minor.documentsExpireAt && (
          <Label style={{ color: stepTextColor, marginTop: 4, fontStyle: 'italic' }}>
            {t('documents-expire-at', { value: formatDate(minor.documentsExpireAt) })}
          </Label>
        )}
        {renderStatusReason(minor.status, minor.reviewReason, minor.invalidationReason)}
        {showDeleteMinor && (
          <Pressable
            disabled={isDeleting}
            onPress={() => deleteMinor(minor)}
            style={{ alignSelf: 'flex-start', marginTop: 6, opacity: isDeleting ? 0.5 : 1 }}
          >
            <Label style={{ color: textDarkTurquoiseTwo, fontSize: 13, lineHeight: 17 }}>
              {t('delete-related-minor-profile')}
            </Label>
          </Pressable>
        )}
      </View>
    );
  };

  const renderMinorForm = () => (
    <View style={{ marginTop: 12 }}>
      {renderMinorFieldRow(t('id-verification-minor-name-field'), minorNameValue, openMinorNamePopup)}
      {renderMinorFieldRow(t('date_of_birth'), minorForm.birthDate ? formatDate(new Date(`${minorForm.birthDate}T00:00:00`)) : undefined, openMinorBirthDatePopup, faCalendar)}
      {renderMinorFieldRow(t('gender'), genderLabel(minorForm.gender), () => openSelector('gender'), faChevronDown)}
      {renderMinorFieldRow(t('relationship'), relationshipLabel(minorForm.relationship), () => openSelector('relationship'), faChevronDown)}
      <Label style={{ color: stepTextColor, fontSize: 14, lineHeight: 18, marginTop: 16 }} numberOfLines={3}>
        {t('id-verification-minor-documents-step-body')}
      </Label>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginTop: 16 }}>
        {renderStepFileButton('minor-front-document-step', t('id-verification-front-short'), minorForm.frontDocument, (value) => updateMinor('frontDocument', value))}
        {renderStepFileButton('minor-back-document-step', t('id-verification-back-short'), minorForm.backDocument, (value) => updateMinor('backDocument', value))}
        {renderStepFileButton('minor-photo-document-step', t('id-verification-photo-short'), minorForm.photoDocument, (value) => updateMinor('photoDocument', value))}
      </View>
      {!!minorMessage && (
        <View style={{ paddingTop: 12 }}>
          <FieldErrorMessage message={minorMessage} />
        </View>
      )}
      <View style={{ flexDirection: 'row', gap: 8, marginTop: 18 }}>
        <CustomButton secondary style={{ flexGrow: 1 }} title={t('cancel')} onPress={resetMinorForm} />
        <CustomButton
          style={{ flexGrow: 1 }}
          disabled={minorSubmitting}
          title={t('submit-related-minor')}
          onPress={validateAndSaveMinor}
        />
      </View>
    </View>
  );

  const renderMinorFieldModal = () => {
    if (!minorEditField) {
      return null;
    }

    const isNameField = minorEditField === 'name';

    return (
      <Modal animationType="none" visible transparent onRequestClose={closeMinorFieldPopup}>
        <Overlay showOverLay zIndex={3000} onPress={closeMinorFieldPopup}>
          <ProfileModal
            title={isNameField ? t('id-verification-minor-name-field') : t('date_of_birth')}
            subtitle={isNameField ? t('id-verification-minor-name-popup-subtitle') : undefined}
            onClose={closeMinorFieldPopup}
            onConfirm={saveMinorFieldPopup}
            showCancel
            onCancel={closeMinorFieldPopup}
          >
            <View>
              {isNameField ? (
                <>
                  <Label style={{ marginBottom: 8 }}>{t('first_name')}</Label>
                  <CustomTextInput
                    required
                    placeholder=""
                    value={minorNameDraft.firstName}
                    onChangeText={(value) => {
                      setMinorFieldMessage(undefined);
                      setMinorNameDraft((current) => ({ ...current, firstName: value }));
                    }}
                  />
                  <Label style={{ marginTop: 16, marginBottom: 8 }}>{t('last_name')}</Label>
                  <CustomTextInput
                    required
                    placeholder=""
                    value={minorNameDraft.lastName}
                    onChangeText={(value) => {
                      setMinorFieldMessage(undefined);
                      setMinorNameDraft((current) => ({ ...current, lastName: value }));
                    }}
                  />
                </>
              ) : (
                <CustomDatePicker
                  selectedDate={minorBirthDateDraft}
                  placeholder={t('date_of_birth')}
                  maxDate={new Date()}
                  onChangeText={(value) => {
                    setMinorFieldMessage(undefined);
                    setMinorBirthDateDraft(value);
                  }}
                />
              )}
              {!!minorFieldMessage && (
                <View style={{ marginTop: 12 }}>
                  <FieldErrorMessage message={minorFieldMessage} />
                </View>
              )}
            </View>
          </ProfileModal>
        </Overlay>
      </Modal>
    );
  };

  const renderSelectorModal = () => {
    if (!selectorField) {
      return null;
    }

    const isGenderSelector = selectorField === 'gender';
    const title = isGenderSelector ? t('gender') : t('relationship');
    const options = isGenderSelector ? genderOptions : relationshipOptions;
    const currentValue = isGenderSelector ? minorForm.gender : minorForm.relationship;
    const handleSelect = (value: RelatedMinorGender | RelatedMinorRelationship) => {
      if (isGenderSelector) {
        updateMinor('gender', value as RelatedMinorGender);
      } else {
        updateMinor('relationship', value as RelatedMinorRelationship);
      }

      closeSelector();
    };

    return (
      <Modal visible transparent animationType="fade" onRequestClose={closeSelector}>
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 16 }}>
          <Pressable
            style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundColor: 'rgba(0, 0, 0, 0.35)' }}
            onPress={closeSelector}
          />
          <View style={{ width: '100%', maxWidth: 360, backgroundColor: backgroundModal, borderRadius: 8, padding: 16 }}>
            <Label variant={LabelType.titleDark}>{title}</Label>
            <View style={{ marginTop: 12, gap: 8 }}>
              {options.map((option) => {
                const selected = option.value === currentValue;
                return (
                  <Pressable
                    key={`${option.value}`}
                    onPress={() => handleSelect(option.value)}
                    style={{
                      borderWidth: 1,
                      borderColor: selected ? backgroundSea : theme.borderColorLight,
                      backgroundColor: selected ? backgroundLightSea : backgroundPrimary,
                      borderRadius: 6,
                      minHeight: 42,
                      justifyContent: 'center',
                      paddingHorizontal: 12,
                    }}
                  >
                    <Label style={{ color: selected ? textDarkTurquoiseTwo : textPrimary, fontWeight: selected ? '700' : '400' }}>
                      {option.label}
                    </Label>
                  </Pressable>
                );
              })}
            </View>
            <CustomButton secondary style={{ marginTop: 16 }} title={t('cancel')} onPress={closeSelector} />
          </View>
        </View>
      </Modal>
    );
  };

  const renderIntro = () => {
    const nativeBottomInset = Platform.OS === 'web' ? 0 : Math.max(insets.bottom, 0);
    const introImageWidth = Math.min(layout.width * 1.18, 470);
    const introImageHeight = Math.min(Math.max(layout.height * 0.44, 330), 500);
    const introContentMinHeight = Math.max(layout.height - insets.top, 640);

    return (
      <View style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, backgroundColor: '#035164' }}>
        <LinearGradient
          colors={['#035164', '#0a6f78', '#5fc8ba']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={StyleSheet.absoluteFillObject}
        />
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{
            flexGrow: 1,
            minHeight: introContentMinHeight,
            paddingTop: Math.max(insets.top, Platform.OS === 'android' ? 30 : 0) + 16,
            paddingHorizontal: 18,
            paddingBottom: Platform.OS === 'web' ? 18 : 0,
          }}
        >
          <MyAccountNavigationButton
            title={backNavigationProps.navigationTitle}
            action={backNavigationProps.navigationAction}
            onPress={backNavigationProps.navigationOnPress}
            iconColor="#8bded7"
            navigationTitleColor="rgba(255,255,255,0.58)"
          />

          <View style={{ alignItems: 'center', marginTop: 16 }}>
            <Label
              variant={LabelType.titleLargeNoCapsPrimary}
              style={{ color: theme.primary, textAlign: 'center', fontSize: 22, lineHeight: 27, fontWeight: '800' }}
            >
              {t('identity-verification')}
            </Label>
            <Label style={{ color: 'white', textAlign: 'center', fontSize: 16, lineHeight: 21, marginTop: 16, maxWidth: 320 }} numberOfLines={4}>
              {t('id-verification-intro-description')}
            </Label>
            <Label style={{ color: 'white', textAlign: 'center', fontSize: 15, lineHeight: 20, marginTop: 22, maxWidth: 340 }} numberOfLines={6}>
              {t('id-verification-intro-body')}
            </Label>

            <CustomButton
              title={t('id-verification-start-verification')}
              onPress={openAdultVerificationForm}
              style={{ width: 220, maxWidth: '100%', marginTop: 28 }}
              textStyle={{ color: 'white' }}
            />
            <CustomButton
              secondary
              title={t('id-verification-later')}
              onPress={goBack}
              style={{ width: 220, maxWidth: '100%', marginTop: 10, borderColor: 'white', backgroundColor: 'transparent' }}
              textStyle={{ color: 'white' }}
            />
          </View>

          <View style={{ flexGrow: 1, justifyContent: 'flex-end', alignItems: 'center', marginTop: 22, marginHorizontal: -18, marginBottom: Platform.OS === 'web' ? -18 : 0 }}>
            <Image
              source={require('../../../../assets/ID-validation.png')}
              resizeMode="contain"
              style={{
                width: introImageWidth,
                height: introImageHeight,
                marginLeft: 28,
                marginBottom: -nativeBottomInset,
                transform: [{ translateY: nativeBottomInset }],
              }}
            />
            <View
              style={{
                position: 'absolute',
                left: 32,
                right: 32,
                bottom: nativeBottomInset + 20,
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <View
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 13,
                  borderWidth: 2,
                  borderColor: 'white',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: 10,
                }}
              >
                <Label style={{ color: 'white', fontWeight: '800', fontSize: 17, lineHeight: 19 }}>!</Label>
              </View>
              <Label style={{ color: 'white', flex: 1, fontWeight: '700', fontSize: 13, lineHeight: 17 }} numberOfLines={4}>
                {t('id-verification-intro-retention')}
              </Label>
            </View>
          </View>
        </ScrollView>
      </View>
    );
  };

  return (
    <>
      {statusQuery.isLoading && (
        <MyAccountPageWrapper title={t('identity-verification')} titleAccessory={titleHelpButton} {...backNavigationProps}>
          <Loading style={{ minHeight: 120, position: 'relative' }} />
        </MyAccountPageWrapper>
      )}
      {!statusQuery.isLoading && shouldShowIntro && renderIntro()}
      {!statusQuery.isLoading && !shouldShowIntro && shouldShowVerificationSteps && (
        <MyAccountPageWrapper title={t('identity-verification')} titleAccessory={titleHelpButton} {...backNavigationProps}>
          {renderVerificationSteps()}
        </MyAccountPageWrapper>
      )}
      {!statusQuery.isLoading && !shouldShowIntro && !shouldShowVerificationSteps && (
        <MyAccountPageWrapper
          title={t('identity-verification')}
          titleAccessory={titleHelpButton}
          {...backNavigationProps}
          scrollOnChange={showMinorForm}
        >
          <ScrollView contentContainerStyle={{ paddingBottom: bottomContentPadding }}>
            <>
              {!!message && (
                <View style={{ paddingTop: 8, marginBottom: 16 }}>
                  <FieldErrorMessage message={message} />
                </View>
              )}

              <InfoBox
                title={t('adult-id-verification')}
                style={{ marginTop: 12 }}
                titleAccessory={
                  <MoreInfoButton
                    title={t('adult-id-verification-help-title')}
                    size={17}
                    openURL={'id-verification-lanla-website-support-link'}
                    style={{ marginLeft: 8 }}
                  >
                    {t('adult-id-verification-help-content')}
                  </MoreInfoButton>
                }
              >
                {renderAdultStatus(adultVerification)}
                <Label style={{ marginTop: 12 }}>{t('documents-retention-notice')}</Label>
                {!adultVerification?.isSoftVerified && <Label style={{ marginTop: 12, color: theme.colorErrorPrimary }}>{t('soft-verification-required')}</Label>}
                {canSubmitAdult && (
                  <View style={{ marginTop: 12 }}>
                    {renderFileInput('adult-front-document', t('front-document'), adultFront, setAdultFront)}
                    {renderFileInput('adult-back-document', t('back-document'), adultBack, setAdultBack)}
                    <CustomButton
                      style={{ marginTop: 16 }}
                      disabled={!adultFront || !adultBack || adultSubmitting}
                      title={t('submit-documents')}
                      onPress={submitAdultDocuments}
                    />
                  </View>
                )}
              </InfoBox>

              <InfoBox
                title={t('related-minors')}
                style={{ marginTop: 24 }}
                titleAccessory={
                  <MoreInfoButton
                    title={t('related-minor-management-help-title')}
                    size={17}
                    style={{ marginLeft: 8 }}
                  >
                    {t('related-minor-management-help-content')}
                  </MoreInfoButton>
                }
              >
                {!adultApproved && <Label>{t('adult-id-required-before-minors')}</Label>}
                {relatedMinors.length === 0 && <Label style={{ marginTop: adultApproved ? 0 : 12 }}>{t('no-related-minors')}</Label>}
                {relatedMinors.map(renderMinorRow)}
                {adultApproved && !showMinorForm && (
                  <CustomButton secondary style={{ marginTop: 16 }} title={t('add-related-minor')} onPress={startMinorAdd} />
                )}
                {adultApproved && showMinorForm && renderMinorForm()}
              </InfoBox>
              {renderSelectorModal()}
            </>
          </ScrollView>
        </MyAccountPageWrapper>
      )}
    </>
  );
};

export default IdVerification;
